import { useCallback, useState } from "react";
import { FRAMERATE_NAMES } from "@infoflyt/shared/timecode";
import type { Oekt } from "../../state/session";
import * as oppsett from "../../state/oppsett";
import { IkkeDin, Topp } from "./deler";
import { UtstyrScreen } from "../UtstyrScreen";

/**
 * Oppsett · Teknisk.
 *
 * Framerate, drop-frame og utstyret. Teknikerens, fordi det er hen som vet
 * hva de skal være — prodleder skal ikke måtte gjette på om det er 25 eller
 * 50. Prodleder og produsent ser den også, og vernet ligger i basen
 * uansett: teknikeren når disse to feltene på produksjonen og ingen andre.
 * Se migrasjon 20261027160000.
 *
 * ========  UTSTYRET HØRER HIT  ========
 *
 * Det sto som egen fane i arbeidsflata fram til 27.10.2026. Men utstyret
 * fylles når prosjektet settes opp, og endres sjelden etter det — og
 * kameraplanen slår opp i det uten at noen må stå på fana. En fane man blar
 * forbi hver dag for noe man gjorde i august er en fane i veien.
 *
 * ⚠ Det ENE unntaket står igjen: A-foto legger til utstyr fra «+ Add new» i
 * kameraplanen, midt på sett. Den veien er urørt, og den er den som er
 * tidskritisk.
 */
export function OppsettTeknisk({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  if (!oppsett.minePunkter(oekt.member.role, oekt.member.extraRoles ?? []).includes("teknisk")) {
    return <IkkeDin />;
  }
  return (
    <div className="flate flate--oppsett">
      <div className="flate__innhold">
        <Topp
          tittel="Teknisk"
          under={`${oekt.production.name} · takten hele produksjonen regner i`}
        />
        <div className="oppsett__rutenett">
          <TekniskKort oekt={oekt} />
        </div>

        <section className="oppsett__bolk">
          <h2 className="oppsett__bolknavn">Utstyr</h2>
          <p className="merknad oppsett__bolktekst">
            Kameraplanene slår opp her og lagrer aldri navnet selv: får et
            utstyr nytt navn, får det nytt navn i alle planer.
          </p>
          <UtstyrScreen oekt={oekt} />
        </section>
      </div>
    </div>
  );
}

function TekniskKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [fps, setFps] = useState(oekt.production.framerate);
  const [drop, setDrop] = useState(oekt.production.dropFrame);
  const [feil, setFeil] = useState<string | null>(null);

  const lagre = useCallback((felt: Record<string, unknown>): void => {
    void (async () => {
      const svar = await oppsett.lagreProduksjon(oekt.production.id, felt);
      setFeil(svar.ok ? null : svar.feil ?? "Ble ikke lagret.");
    })();
  }, [oekt.production.id]);

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Teknisk</span>
        <span className="loggkort__lag">A-foto</span>
      </div>
      <div className="oppsett__felt">
        <label className="oppsett__etikett" htmlFor="opp-fps">Framerate</label>
        <select
          id="opp-fps" className="meny__velger" value={fps}
          onChange={(e) => {
            const v = e.target.value as typeof fps;
            setFps(v);
            lagre({ framerate: v });
          }}
        >
          {FRAMERATE_NAMES.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      <div className="oppsett__felt">
        <label className="oppsett__etikett" htmlFor="opp-drop">Drop-frame</label>
        <input
          id="opp-drop" type="checkbox" checked={drop}
          onChange={(e) => { setDrop(e.target.checked); lagre({ drop_frame: e.target.checked }); }}
        />
      </div>
      {/*
        * HELE PRODUKSJONEN REGNER LIKT. To regier på samme produksjon som
        * regner ulikt peker ulike steder i materialet.
        */}
      <p className="merknad oppsett__merknad">
        Gjelder hele produksjonen. Endrer du den, står tidskoden du ser
        stille — ankeret regnes om, så postene beholder klokkeslettet sitt.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}
